/**
 * Le parti della consegna (GI-237): cio' che si legge di una bozza — rilievi,
 * riepilogo, contenuto, due scale, cifre del modello, soglie — e la ricevuta.
 * Vivono qui, fuori dalla pagina, perche' la pagina e' un elenco di tappe e
 * queste sono cio' che ogni tappa apre.
 */
import { Link } from 'react-router-dom'

import { StatusPill } from '../carta/StatusPill'
import { Severity } from '../design/indicators'
import type * as exports from '../exports'
import { decimal, moment, notMeasured, share as percent } from '../format'
import { useT } from '../i18n/LocaleProvider'
import type { QualitySeal } from '../quality'
import type { Draft, Item, ModelMetrics, Problem, Threshold } from '../types'

export function withdrawn(draft: Draft, seal: QualitySeal | null | 'missing'): boolean {
  if (draft.invalidated_at) return true
  if (seal === 'missing' || seal === null || !seal.valid) return true
  if (draft.review_epoch != null && draft.review_epoch !== seal.review_epoch) return true
  if (
    draft.frozen_review_version != null &&
    draft.frozen_review_version !== seal.frozen_review_version
  ) {
    return true
  }
  if (
    draft.supervisor_review_version != null &&
    draft.supervisor_review_version !== seal.supervisor_review_version
  ) {
    return true
  }
  if (draft.quality_seal_id != null && draft.quality_seal_id !== seal.id) return true
  return false
}

export function Blockers({ draft }: { draft: Draft }): JSX.Element {
  const t = useT()
  return (
    <section>
      {draft.not_deliverable_findings > 0 && (
        <p className="avviso">
          {t('delivery.undeliverable', { n: draft.not_deliverable_findings })}
        </p>
      )}
      {draft.errors.length > 0 && (
        <Problems title={t('delivery.errorsTitle')} problems={draft.errors} kind="errore" />
      )}
      {draft.warnings.length > 0 && (
        <Problems title={t('delivery.warningsTitle')} problems={draft.warnings} kind="avviso" />
      )}
      {draft.errors.length === 0 && draft.warnings.length === 0 && (
        <p className="tenue">{t('delivery.noIssues')}</p>
      )}
    </section>
  )
}

export function PermanentReceipt({
  draft,
  file,
  onDocument,
}: {
  draft: Draft
  file: string | null
  onDocument: () => void
}): JSX.Element {
  const t = useT()
  const submitted = draft.state === 'submitted' || draft.state === 'accepted' || draft.state === 'rejected'
  return (
    <section className="ricevuta">
      <div className="riga-ricevuta">
        <StatusPill
          label={submitted ? t('pool.statusDelivered') : draft.state}
          tone={submitted ? 'st-lock' : 'ink-3'}
        />
        <span className="t-num">{draft.sent_at ? moment(draft.sent_at) : notMeasured()}</span>
      </div>
      <dl className="t-num">
        <dt>{t('delivery.declarations')}</dt>
        <dd>{decimal(draft.issues)}</dd>
        <dt>{t('delivery.operator')}</dt>
        <dd>{draft.sent_by ?? notMeasured()}</dd>
        <dt>{t('delivery.catalog')}</dt>
        <dd>{draft.catalog_version}</dd>
        <dt>{t('delivery.sha256')}</dt>
        <dd>{draft.checksum}</dd>
      </dl>
      <p>
        {file === null ? (
          <button type="button" onClick={onDocument}>
            {t('delivery.prepareDoc')}
          </button>
        ) : (
          <a href={file} download={`consegna-${draft.checksum.slice(0, 8)}.json`}>
            {t('delivery.downloadDoc')}
          </a>
        )}
      </p>
      <p>
        <Link to={`/exports/${draft.export_id}`}>{t('delivery.openReceipt')}</Link>
      </p>
      {draft.warning_note && (
        <p>
          <span className="tenue">{t('delivery.sentWithWarnings')}</span>
          {draft.warning_note}
        </p>
      )}
      {draft.rejection_reason && (
        <p className="guasto">{t('delivery.rejected', { reason: draft.rejection_reason })}</p>
      )}
    </section>
  )
}

export function Summary({ draft }: { draft: Draft }): JSX.Element {
  const t = useT()
  return (
    <section>
      <p>
        {t('delivery.stateIssues', {
          state: draft.state,
          n: draft.issues,
          catalog: draft.catalog_version,
        })}
      </p>
      <p className="tenue">
        {t('delivery.checksum', { checksum: draft.checksum.slice(0, 12) })}
        {draft.supersedes_id && t('delivery.redo')}
      </p>
      {draft.frozen_review_version != null && (
        <p className="tenue">
          {t('delivery.sealLine', {
            epoch: draft.review_epoch ?? '',
            freeze: draft.frozen_review_version,
            supervisor: draft.supervisor_review_version ?? '',
          })}
        </p>
      )}
    </section>
  )
}

export function Contents({ items }: { items: Item[] }): JSX.Element {
  const t = useT()
  return (
    <section>
      <h3>{t('delivery.contents', { n: items.length })}</h3>
      {items.length === 0 ? (
        <p className="tenue">{t('delivery.emptyContents')}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('delivery.colElement')}</th>
              <th>{t('delivery.colType')}</th>
              <th>{t('delivery.colCode')}</th>
              <th>{t('delivery.colLevel')}</th>
              <th>{t('delivery.colImages')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((one, index) => (
              <tr key={`${one.element_external_id}-${one.issue_type_code}-${index}`}>
                <td>{one.element_external_id}</td>
                <td>{one.element_type}</td>
                <td>
                  {one.issue_type_description === '' ? (
                    one.issue_type_code
                  ) : (
                    <>
                      {one.issue_type_description}
                      <br />
                      <span className="tenue">{one.issue_type_code}</span>
                    </>
                  )}
                </td>
                <td>
                  {/* Il livello come lo vede ENEL e' un numero; qui accanto
                      al numero sta il rango, con lo stesso segno del resto
                      dell'applicazione (GI-224). */}
                  <Severity level={one.evaluation_level} />
                </td>
                <td>{one.images}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

export function Scales({ scales }: { scales: exports.Scales }): JSX.Element {
  const t = useT()
  const verdict = (which: string): string => {
    if (which === 'agree') return t('delivery.verdict.agree')
    if (which === 'diverge') return t('delivery.verdict.diverge')
    return t('delivery.verdict.incomparable')
  }
  return (
    <section>
      <h3>{t('delivery.scalesTitle')}</h3>
      <p className="tenue">{t('delivery.scalesWhy')}</p>
      <p>
        {t('delivery.scalesFrontier', {
          kv: scales.voltage_kv,
          touch: scales.our_touch_m.toFixed(2),
          urgent: scales.their_urgent_to_m.toFixed(2),
          limit: scales.our_limit_m.toFixed(2),
          table: scales.their_table_to_m.toFixed(2),
        })}
      </p>
      <p>
        {t('delivery.scalesCounts', {
          agree: scales.agreeing,
          diverge: scales.diverging,
          incomparable: scales.incomparable,
        })}
      </p>
      {scales.unexplained > 0 && (
        <p className="avviso">{t('delivery.scalesUnexplained', { n: scales.unexplained })}</p>
      )}
      {scales.rows.length === 0 ? (
        <p className="tenue">{t('delivery.scalesNone')}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('delivery.colElement')}</th>
              <th>{t('delivery.colDistance')}</th>
              <th>{t('delivery.colOurs')}</th>
              <th>{t('delivery.colTheirs')}</th>
              <th>{t('delivery.colVerdict')}</th>
            </tr>
          </thead>
          <tbody>
            {scales.rows.map((one, index) => (
              <tr key={`${one.element_external_id}-${one.ours}-${index}`}>
                <td>{one.element_external_id}</td>
                <td>{one.distance_m.toFixed(2)} m</td>
                <td>{one.ours || '—'}</td>
                <td>{one.theirs || '—'}</td>
                <td>
                  {verdict(one.verdict)}
                  {one.why && <span className="tenue"> · {one.why}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

export function Problems({
  title,
  problems,
  kind,
}: {
  title: string
  problems: Problem[]
  kind: string
}): JSX.Element {
  const t = useT()
  return (
    <section>
      <h3>{title}</h3>
      <ul className={kind === 'errore' ? 'rilievi errori' : 'rilievi'}>
        {problems.map((problem, index) => (
          <li key={index}>
            <code>{problem.rule}</code>
            {problem.issue !== null && (
              <span className="tenue">{t('delivery.issueIndex', { n: problem.issue + 1 })}</span>
            )}
            <div>{problem.message}</div>
            {problem.hypothesis && (
              <div className="tenue">
                {t('delivery.onHypothesis', { name: problem.hypothesis })}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

export function Metrics({ metrics }: { metrics: ModelMetrics }): JSX.Element {
  const t = useT()
  const share = (value: number | null): string => (value === null ? notMeasured() : percent(value))
  return (
    <section>
      <h3>
        {t('delivery.modelTitle', {
          version: metrics.model_version ?? t('delivery.modelNone'),
        })}
      </h3>
      <ul className="rilievi">
        <li>
          {t('delivery.modelMetrics', {
            r1: share(metrics.r1),
            r2: share(metrics.r2),
            error: share(metrics.error_rate),
          })}
          <div className="tenue">
            {t('delivery.modelCounts', {
              declared: metrics.declared,
              predicted: metrics.predicted,
              matched: metrics.matched,
            })}
          </div>
          {metrics.shortfall.length > 0 && (
            <div className="tenue">
              {t('delivery.modelMissing', { list: metrics.shortfall.join(' · ') })}
            </div>
          )}
        </li>
      </ul>
      <p className="tenue">{t('delivery.modelNote')}</p>
    </section>
  )
}

export function Thresholds({ thresholds }: { thresholds: Threshold[] }): JSX.Element {
  const t = useT()
  return (
    <section>
      <h3>{t('delivery.annex5')}</h3>
      <ul className="rilievi">
        {thresholds.map((threshold) => (
          <li key={threshold.name}>
            <code>{threshold.name}</code>{' '}
            <span className={threshold.passed ? 'tenue' : 'avviso'}>
              {threshold.measured ?? '—'} / {threshold.limit}
            </span>
            <div className="tenue">
              {threshold.basis === 'norm' ? t('delivery.fromCustomer') : t('delivery.ourHypothesis')}
              {threshold.note && ` — ${threshold.note}`}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
